import React from "react";
import { Link, useParams } from "react-router-dom";
import { projects, Picture } from "../generated/content";
import { useLanguage } from "../context/LanguageContext";
import { projectMeta } from "../lib/projectMeta";
import Header from "./Header";
import Footer from "./Footer";
import NotFoundScreen from "./NotFoundScreen";
import "../style/ProjectScreen.css";

/**
 * Group the gallery into one full-width image followed by a pair, repeating.
 * A uniform grid gives every room the same weight; alternating the rhythm
 * keeps a long gallery from flattening out.
 */
function groupImages(images: Picture[]) {
  const rows: { kind: "wide" | "pair"; items: Picture[] }[] = [];
  let index = 0;

  while (index < images.length) {
    rows.push({ kind: "wide", items: [images[index]] });
    index += 1;

    const pair = images.slice(index, index + 2);
    if (pair.length === 2) {
      rows.push({ kind: "pair", items: pair });
      index += 2;
    } else if (pair.length === 1) {
      rows.push({ kind: "wide", items: pair });
      index += 1;
    }
  }

  return rows;
}

export default function ProjectScreen() {
  const { slug } = useParams<{ slug: string }>();
  const { lang, t } = useLanguage();

  const index = projects.findIndex((project) => project.slug === slug);
  if (index === -1) return <NotFoundScreen />;

  const project = projects[index];
  const previous = projects[index - 1];
  const next = projects[index + 1];

  const meta = projectMeta(project, lang);
  const description = t(project.description);
  const paragraphs = description
    .split(/\n\s*\n|\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const rows = groupImages(project.images);

  return (
    <>
      <Header />

      <section className="project-intro">
        <div className="project-intro__inner">
          <Link className="project-intro__back" to="/">
            ← {lang === "en" ? "All projects" : "Tüm projeler"}
          </Link>

          <h1>{t(project.title)}</h1>
          {meta && <p className="meta-line project-intro__meta">{meta}</p>}

          {paragraphs.map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}
        </div>
      </section>

      <section className="gallery" aria-label={t(project.title)}>
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className={row.kind === "pair" ? "gallery__pair" : "gallery__wide"}>
            {row.items.map((picture) => (
              <figure key={picture.src}>
                <div className="gallery__frame">
                  <img
                    src={picture.src}
                    srcSet={picture.srcSet}
                    // Full width on a wide row, half on a pair, full on phones
                    // where pairs collapse to one column.
                    sizes={
                      row.kind === "pair"
                        ? "(max-width: 700px) 100vw, 50vw"
                        : "100vw"
                    }
                    alt={t(project.title)}
                    loading="lazy"
                  />
                </div>
              </figure>
            ))}
          </div>
        ))}
      </section>

      {(previous || next) && (
        <nav className="project-nav" aria-label={lang === "en" ? "Other projects" : "Diğer projeler"}>
          {previous ? (
            <Link to={`/projects/${previous.slug}`}>
              <small>{lang === "en" ? "Previous" : "Önceki"}</small>
              {t(previous.title)}
            </Link>
          ) : (
            <span />
          )}
          {next && (
            <Link className="project-nav__next" to={`/projects/${next.slug}`}>
              <small>{lang === "en" ? "Next" : "Sonraki"}</small>
              {t(next.title)}
            </Link>
          )}
        </nav>
      )}

      <Footer />
    </>
  );
}
